<!--
  AdminUserDashboard.svelte — Configure the non-admin user dashboard.

  Controls the WordPress news feed proxy: enable/disable, feed URL,
  category filter, and cache TTL. Saves via the global updateSettings() call.
-->
<script lang="ts">
	import { Button, Input, Label, Toggle, Heading, Spinner, Helper } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { onMount } from 'svelte';
	import { getSettings, updateSettings, probeNewsFeedUrl } from '../../AppNetwork';
	import type { Settings } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Current settings loaded from server. */
	let settings: Settings | null = null;
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Whether a save operation is in progress. */
	let saving = $state(false);
	/** Error message from the last operation. */
	let error = $state('');

	// ── Form fields ──────────────────────────────────────────────

	/** Master toggle for the news feed on the user dashboard. */
	let newsFeedEnabled = $state(false);
	/** WordPress JSON API URL for fetching posts. */
	let newsFeedUrl = $state('');
	/** Comma-separated WordPress category IDs to filter the feed. */
	let newsFeedCategories = $state('');
	/** Cache time-to-live in seconds. */
	let newsFeedCacheTTL = $state(900);
	/** Whether a URL probe operation is in progress. */
	let probing = $state(false);

	/**
	 * Load the current settings from the server and populate the form.
	 */
	onMount(async () => {
		try {
			const s = await getSettings();
			settings = s;
			if (settings) populateForm(settings);
			else error = t2('No settings found.');
		} catch (err) {
			error = err instanceof Error ? err.message : t2('Failed to load settings');
		} finally {
			loading = false;
		}
	});

	/**
	 * Populate form fields from a Settings object.
	 * @param s - The settings object to read from.
	 */
	function populateForm(s: Settings): void {
		newsFeedEnabled = s.newsFeedEnabled;
		newsFeedUrl = s.newsFeedUrl || '';
		newsFeedCategories = s.newsFeedCategories || '';
		newsFeedCacheTTL = s.newsFeedCacheTTL;
	}

	/**
	 * Save the current form values to the server.
	 */
	async function handleSave(): Promise<void> {
		saving = true;
		error = '';

		/** Send the updated feed settings to the server and refresh the form. */
		try {
			settings = await updateSettings({
				newsFeedEnabled,
				newsFeedUrl: newsFeedUrl || null,
				newsFeedCategories: newsFeedCategories || null,
				newsFeedCacheTTL
			});
			populateForm(settings);
			toastSuccess(t2('User dashboard settings saved.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to save settings'));
		} finally {
			saving = false;
		}
	}

	/**
	 * Probe the entered URL/domain for a valid WordPress REST API endpoint.
	 * On success, auto-populates the Feed URL field with the resolved URL.
	 */
	async function handleProbe(): Promise<void> {
		if (!newsFeedUrl.trim()) return;
		probing = true;

		/** Probe the server and update the URL field on success. */
		try {
			const resolved = await probeNewsFeedUrl(newsFeedUrl.trim());
			newsFeedUrl = resolved;
			toastSuccess(t2('WordPress REST API detected successfully.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Could not detect a WordPress REST API at this URL.'));
		} finally {
			probing = false;
		}
	}
</script>

<Heading tag="h3">{$t("User Dashboard")}</Heading>

{#if loading}
	<div class="flex items-center justify-center py-12">
		<Spinner size="8" />
	</div>
{:else}
	<div class="mt-4 space-y-6">
		{#if error}
			<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
			</div>
		{/if}

		<!-- News Feed Configuration -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-rss me-2"></i>{$t("News Feed")}
			</h4>
			<Helper class="mb-4">
				{$t("Display a WordPress news feed on the non-admin user dashboard.")}
				{$t("Uses the WordPress REST API")} (<code>wp-json/wp/v2/posts</code>) {$t("to fetch recent posts.")}
			</Helper>

			<div class="mb-4 flex items-center gap-3">
				<Toggle bind:checked={newsFeedEnabled} />
				<div>
					<p class="font-medium text-gray-900 dark:text-white">{$t("Enable News Feed")}</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">
						{$t("When disabled, the feed section is hidden from the user dashboard.")}
					</p>
				</div>
			</div>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div class="md:col-span-2">
					<Label for="newsFeedUrl" class="mb-2">{$t("Feed URL")}</Label>
					<div class="flex gap-2">
						<div class="flex-1">
							<Input
								id="newsFeedUrl"
								bind:value={newsFeedUrl}
								placeholder="abstract3d.com"
								disabled={!newsFeedEnabled}
							/>
						</div>
						<Button
							size="sm"
							color="alternative"
							onclick={handleProbe}
							disabled={!newsFeedEnabled || probing || !newsFeedUrl.trim()}
						>
							{#if probing}
								<i class="fa-solid fa-spinner fa-spin me-1"></i>{$t("Detecting...")}
							{:else}
								<i class="fa-solid fa-magnifying-glass me-1"></i>{$t("Detect")}
							{/if}
						</Button>
					</div>
					<Helper class="mt-1">
						{$t("Enter a domain or URL — click")} <strong>{$t("Detect")}</strong> {$t("to auto-resolve the WordPress REST API endpoint.")}
					</Helper>
				</div>

				<div>
					<Label for="newsFeedCategories" class="mb-2">{$t("Category IDs")}</Label>
					<Input
						id="newsFeedCategories"
						bind:value={newsFeedCategories}
						placeholder="49366370,49366360"
						disabled={!newsFeedEnabled}
					/>
					<Helper class="mt-1">
						{$t("Comma-separated WordPress category IDs. Leave empty to show all categories.")}
					</Helper>
				</div>

				<div>
					<Label for="newsFeedCacheTTL" class="mb-2">{$t("Cache TTL (seconds)")}</Label>
					<Input
						id="newsFeedCacheTTL"
						type="number"
						bind:value={newsFeedCacheTTL}
						placeholder="900"
						disabled={!newsFeedEnabled}
					/>
					<Helper class="mt-1">
						{$t("How long to cache feed results server-side. Default: 900 (15 minutes).")}
					</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Save -->
		<div class="flex justify-end">
			<Button onclick={handleSave} disabled={saving}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Saving...")}
				{:else}
					<i class="fa-solid fa-save me-2"></i>{$t("Save Settings")}
				{/if}
			</Button>
		</div>
	</div>
{/if}
